import { normalizeExerciseName } from './exerciseCatalog';
import datasetRef from '../data/dataset-ref.json';

/**
 * Exercise media (demo images + how-to instructions) sourced from the
 * free-exercise-db dataset (yuhonas/free-exercise-db, public domain / Unlicense).
 *
 * The app's exercise_catalog was seeded from this same dataset (see
 * scripts/seed-exercises.js), so global exercises match by normalized name.
 * Custom user exercises won't match and gracefully fall back to a YouTube search.
 *
 * Images are served through the jsDelivr CDN for speed/reliability. Each exercise
 * exposes 1-2 static frames (start / end position) which we alternate in the UI
 * to fake a GIF-like animation.
 */

// Pinned to a specific commit (NOT @main) so production stays in sync with the
// bundled Spanish translations in src/data/instructions.es.json, which were
// generated against this exact snapshot. The ref is the single source of truth
// in src/data/dataset-ref.json (shared with the seed + translation scripts);
// the cache key is derived from it so it self-invalidates when the ref changes.
const DATASET_REF = datasetRef.ref;
const DATA_URL = `https://cdn.jsdelivr.net/gh/yuhonas/free-exercise-db@${DATASET_REF}/dist/exercises.json`;
const IMG_BASE = `https://cdn.jsdelivr.net/gh/yuhonas/free-exercise-db@${DATASET_REF}/exercises/`;
const CACHE_KEY = `exercise-media-db-v2-${DATASET_REF.slice(0, 7)}`;
const STRENGTH_CATEGORY = 'strength' as const;

export interface ExerciseMedia {
  id: string;
  name: string;
  category: typeof STRENGTH_CATEGORY;
  /** Full CDN URLs for each demo frame (usually 2: start + end). */
  images: string[];
  instructions: string[];
}

interface RawExercise {
  id: string;
  name: string;
  category?: string;
  images?: string[];
  instructions?: string[];
}

let indexPromise: Promise<Map<string, ExerciseMedia>> | null = null;

function buildIndex(raw: RawExercise[]): Map<string, ExerciseMedia> {
  const map = new Map<string, ExerciseMedia>();
  for (const ex of raw) {
    if (!ex?.name || ex.category !== STRENGTH_CATEGORY) continue;
    const key = normalizeExerciseName(ex.name);
    if (map.has(key)) continue;
    map.set(key, {
      id: ex.id,
      name: ex.name,
      category: STRENGTH_CATEGORY,
      images: (ex.images || []).map((p) => IMG_BASE + p),
      instructions: ex.instructions || [],
    });
  }
  return map;
}

async function loadIndex(): Promise<Map<string, ExerciseMedia>> {
  // Serve from localStorage cache when available (avoids re-downloading ~hundreds of KB).
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) return buildIndex(JSON.parse(cached));
  } catch {
    /* ignore corrupt cache */
  }

  const res = await fetch(DATA_URL);
  if (!res.ok) throw new Error(`HTTP ${res.status} cargando media de ejercicios`);
  const raw: RawExercise[] = await res.json();
  const strengthExercises = raw.filter(
    (exercise) => exercise.category === STRENGTH_CATEGORY
  );

  // Persist a slimmed copy (only the fields we use) to keep storage small.
  try {
    const slim: RawExercise[] = strengthExercises.map((e) => ({
      id: e.id,
      name: e.name,
      category: e.category,
      images: e.images,
      instructions: e.instructions,
    }));
    localStorage.setItem(CACHE_KEY, JSON.stringify(slim));
  } catch {
    /* quota exceeded — fine, we just won't cache */
  }

  return buildIndex(strengthExercises);
}

/** Lazily load + memoize the name→media index. Retries on failure. */
export function getExerciseMediaIndex(): Promise<Map<string, ExerciseMedia>> {
  if (!indexPromise) {
    indexPromise = loadIndex().catch((err) => {
      indexPromise = null; // allow retry on next call
      throw err;
    });
  }
  return indexPromise;
}

export async function getExerciseMedia(name: string): Promise<ExerciseMedia | null> {
  const index = await getExerciseMediaIndex();
  return index.get(normalizeExerciseName(name)) || null;
}

export type DemoLang = 'es' | 'en';

/**
 * Spanish instructions are pre-translated offline (see scripts/translate-instructions.mjs)
 * into a static JSON keyed by exercise id. Loaded lazily as its own chunk so it never
 * bloats the main bundle, and only fetched the first time the demo is opened in ES.
 */
let esInstructionsPromise: Promise<Record<string, string[]>> | null = null;

export function getInstructionsEsIndex(): Promise<Record<string, string[]>> {
  if (!esInstructionsPromise) {
    esInstructionsPromise = import('../data/instructions.es.json')
      .then((mod) => (mod.default || mod) as Record<string, string[]>)
      .catch((err) => {
        esInstructionsPromise = null;
        throw err;
      });
  }
  return esInstructionsPromise;
}

/**
 * Returns the instructions in the requested language. Falls back to the English
 * source when a Spanish translation is missing for that exercise.
 */
export async function getInstructions(
  media: ExerciseMedia,
  lang: DemoLang
): Promise<{ steps: string[]; lang: DemoLang }> {
  if (lang === 'en') return { steps: media.instructions, lang: 'en' };
  try {
    const es = await getInstructionsEsIndex();
    const translated = es[media.id];
    if (translated && translated.length > 0) return { steps: translated, lang: 'es' };
  } catch {
    /* missing/failed ES bundle — fall back to English */
  }
  return { steps: media.instructions, lang: 'en' };
}

/** Deep-link to a YouTube search — no API key, no quota. */
export function youtubeSearchUrl(name: string): string {
  const query = `como hacer ${name} ejercicio tecnica correcta`;
  return `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`;
}
